/**
 * The Greenvolt logo.
 *
 * Two things make this a component rather than an `<img>` at each call site.
 *
 * **The variant is a contrast decision, not a preference.** The white mark is
 * the only legible one on navy; the colour mark is the only legible one on
 * white. Naming the surface (`on`) rather than the artwork forces the caller to
 * say where the logo sits, and the correct file follows from that.
 *
 * **The design system's two hard constraints are geometric**, so they can be
 * encoded once here instead of being remembered at each call site:
 *
 *   - *Clear space* — minimum margin on all sides equals the height of the "G",
 *     which is the full cap height, i.e. the mark's own height. Nothing may
 *     enter that zone. Applied as `margin`, not `padding`: the artwork is a
 *     background image sized `contain`, so padding would enlarge the painting
 *     area and shrink the mark inside it. Margin reserves the zone in the
 *     surrounding layout and leaves the mark at the height asked for.
 *   - *Minimum size* — 32px height for digital use, with a 20px floor reserved
 *     for favicons and avatars. `height` below 32 is a type error; the 20px
 *     floor is not offered because this component never renders a favicon.
 *
 * The artwork itself is the `--logo-color` / `--logo-white` tokens extracted
 * from the design system in task 5.2 — data URIs, so there is no network
 * request and no flash of missing brand on first paint.
 *
 * The aspect ratio is fixed by `background-size: contain` in `.gv-logo`, so
 * callers give a height and the width follows. That is deliberate: a caller who
 * could set both could distort the mark, which the DS lists under "Logo Misuse
 * — Never Do This".
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.1, §4.5
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

/**
 * The design system's digital minimum, in pixels.
 *
 * The DS also names a 20px "favicon / avatar floor", which this component
 * deliberately does not expose — those are generated assets, not laid-out ones.
 */
export const LOGO_MIN_HEIGHT = 32;

/**
 * Aspect ratio of the wordmark, measured from the extracted artwork itself
 * (the colour PNG is 1100 x 221) rather than copied from the mockup, which
 * rounds its two placements to 5.0 and 4.89 and so distorts the mark slightly
 * in both. Deriving it from the source keeps every placement identical.
 */
const LOGO_ASPECT = 1100 / 221;

export interface GvLogoProps {
  /**
   * The surface the logo sits on, which selects the legible variant. Named for
   * the background rather than for the artwork so that a caller cannot pick
   * "white" while placing it on white.
   */
  on: 'navy' | 'light';
  /**
   * Rendered height in pixels. Width follows from the aspect ratio. Must be at
   * least {@link LOGO_MIN_HEIGHT}.
   */
  height?: number;
  /**
   * Reserve the DS clear-space zone as padding around the mark.
   *
   * Default `true`. Pass `false` only where the surrounding layout already
   * guarantees the margin — the nav rail's own padding, for instance — because
   * doubling it pushes the mark visibly off-grid.
   */
  clearSpace?: boolean;
  className?: string;
}

/**
 * The wordmark, at a given height, on a stated surface.
 *
 * Rendered as a `<span>` with an accessible name rather than an `<img>`: the
 * artwork is a CSS background (that is what the DS tokens are), and a `role`
 * plus label is how a background image gets into the accessibility tree.
 */
export function GvLogo({
  on,
  height = LOGO_MIN_HEIGHT,
  clearSpace = true,
  className,
}: GvLogoProps): React.JSX.Element {
  const { t } = useTranslation();

  /* Clamped rather than rejected. A height below the minimum is a caller bug,
     but rendering an illegibly small logo is worse than rendering a legal one:
     the layout stays intact and the brand stays readable. TypeScript cannot
     catch it because heights are usually computed, not literal. */
  const h = Math.max(height, LOGO_MIN_HEIGHT);

  return (
    <span
      role="img"
      aria-label={t('brand.logoAlt')}
      className={cn('gv-logo', on === 'navy' ? 'white' : 'color', className)}
      style={{
        height: `${String(h)}px`,
        width: `${String(Math.round(h * LOGO_ASPECT))}px`,
        /* Clear space = the height of the "G" = the mark's own height. */
        ...(clearSpace ? { margin: `${String(h)}px` } : {}),
      }}
    />
  );
}
